import { endpoints } from '@/config/api';
import { apiClient } from '@/lib/apiClient';
import type { CreateTripDTO, PaginatedResponse, Trip, TripFilters, UpdateTripDTO } from '@/types';

/**
 * Trip Service - Handles all trip-related API calls
 */

export const tripService = {
  /**
   * Fetch trips with optional filters and pagination
   */
  async getTrips(filters?: TripFilters, page = 1, pageSize = 20): Promise<PaginatedResponse<Trip>> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.searchTerm) params.append('search', filters.searchTerm);
    params.append('page', String(page));
    params.append('pageSize', String(pageSize));

    const url = `${endpoints.trips.list}?${params.toString()}`;
    const response = await apiClient.get<PaginatedResponse<Trip> | Trip[]>(url);

    if (Array.isArray(response)) {
      const total = response.length;
      const startIndex = Math.max(0, (page - 1) * pageSize);
      const endIndex = startIndex + pageSize;
      const pagedData = response.slice(startIndex, endIndex);

      return {
        data: pagedData,
        page,
        pageSize,
        total,
        hasMore: endIndex < total,
      };
    }

    return {
      data: Array.isArray(response.data) ? response.data : [],
      page: response.page ?? page,
      pageSize: response.pageSize ?? pageSize,
      total: response.total ?? (Array.isArray(response.data) ? response.data.length : 0),
      hasMore: response.hasMore ?? false,
    };
  },

  /**
   * Get a single trip by ID
   */
  async getTrip(id: string): Promise<Trip> {
    return apiClient.get<Trip>(endpoints.trips.get(id));
  },

  /**
   * Create a new trip
   */
  async createTrip(trip: CreateTripDTO): Promise<Trip> {
    return apiClient.post<Trip>(endpoints.trips.create, trip);
  },

  /**
   * Update an existing trip
   */
  async updateTrip(id: string, trip: UpdateTripDTO): Promise<Trip> {
    return apiClient.put<Trip>(endpoints.trips.update(id), trip);
  },

  /**
   * Delete a trip
   */
  async deleteTrip(id: string): Promise<void> {
    return apiClient.delete(endpoints.trips.delete(id));
  },
};
